import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Group,
  Stack,
  Alert,
  LoadingOverlay,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconAlertCircle } from '@tabler/icons-react';
import { useMedplum } from '@medplum/react';
import { ConsentProvision } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import {
  ConsentSection,
  ConsentBanner,
  ConsentDisplay,
  SpecimenBox,
  PaymentSection,
} from '../components';
import { OrderFormData } from '../components/OrderForm';
import { useOrderDetails } from '../hooks/useOrderDetails';
import { checkConsentStatus } from '@hooks/useConsent';
import { OrderBox } from '../components/OrderBox';
import { ReportBox } from '../components/ReportBox';
import { OrderInfoBanner } from './OrderInfoBanner';
import classes from './OrderPage.module.css';

// -----------------------------------------------------------------------------
export const OrderPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const medplum = useMedplum();
  const { t } = useTranslation(['lims', 'common']);

  const [
    showConsentModal,
    { open: openConsentModal, close: closeConsentModal },
  ] = useDisclosure(false);
  const [consentStatus, setConsentStatus] = useState<
    ConsentProvision['type'] | undefined
  >(undefined);
  const [consentUpdateKey, setConsentUpdateKey] = useState(0);

  const orderDetails = useOrderDetails(id);
  const {
    serviceRequest,
    getFormDataFromOrder,
    loading: orderLoading,
    loadOrderDetails,
    specimens,
    patient,
    requester,
  } = orderDetails;

  // Invalidate and refetch reports when navigating back from report editor
  useEffect(() => {
    const state = location.state as { reloadReports?: boolean } | null;
    if (state?.reloadReports) {
      medplum.invalidateSearches('DiagnosticReport');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, medplum]);

  const orderFormData = useMemo<OrderFormData>(() => {
    const initial = getFormDataFromOrder();
    return {
      patient: patient ?? undefined,
      requester: requester ?? undefined,
      testCode: '',
      testDisplay: '',
      sampleType: undefined,
      clinicalNotes: '',
      answerDelivery: [],
      authorizedPerson: '',
      authorizationFormFile: undefined,
      orderDate: new Date(),
      orderFile: undefined,
      referralReason: '',
      symptomOnsetAge: '',
      firstSymptom: '',
      ...initial,
    };
  }, [getFormDataFromOrder, patient, requester]);

  // ---------------------------------------------------------------------------
  useEffect(() => {
    const checkConsent = async () => {
      if (!serviceRequest?.id) {
        return;
      }

      const consentStatus = await checkConsentStatus(
        medplum,
        serviceRequest.id
      );

      setConsentStatus(consentStatus);
    };
    checkConsent();
  }, [serviceRequest, medplum]);

  // ---------------------------------------------------------------------------
  if (orderLoading) {
    return (
      <div className="relative h-96">
        <LoadingOverlay visible />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  if (!serviceRequest) {
    return (
      <Stack gap="md" className="p-6">
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          {t('orderDetails.orderNotFound', { ns: 'lims' })}
        </Alert>
        <Button onClick={() => navigate('/lims')}>
          {t('orderDetails.backToOrders', { ns: 'lims' })}
        </Button>
      </Stack>
    );
  }

  // ---------------------------------------------------------------------------
  const handleConsentUpdate = async () => {
    await loadOrderDetails();
    if (!serviceRequest?.id) {
      return;
    }

    const consentStatus = await checkConsentStatus(medplum, serviceRequest.id);

    setConsentStatus(consentStatus);
    setConsentUpdateKey((prev) => prev + 1);
  };

  // ---------------------------------------------------------------------------
  return (
    <Box className={classes.pageWrapper}>
      {/* Sticky Banner */}
      <Box className={classes.stickyBanner}>
        <OrderInfoBanner
          serviceRequest={serviceRequest}
          patient={patient ?? undefined}
          requester={requester ?? undefined}
          formData={orderFormData}
        />
      </Box>

      {/* Scrollable Content */}
      <Box className={classes.scrollableContent}>
        <Stack gap="md" p="md">
          {/* Consent banner when consent is missing */}
          {!consentStatus && (
            <ConsentBanner onCreateConsent={openConsentModal} />
          )}

          <Group align="flex-start" gap="xl" grow>
            <Stack>
              <OrderBox
                patient={patient}
                requester={requester}
                formData={orderFormData}
                serviceRequest={serviceRequest}
                initialFormData={getFormDataFromOrder()}
                onOrderUpdated={loadOrderDetails}
              />
              <SpecimenBox
                specimens={specimens}
                patient={patient}
                serviceRequest={serviceRequest}
                orderFormData={orderFormData}
                onViewSpecimen={(specimen) =>
                  navigate(`/lims/specimen/${specimen.id}`)
                }
                onAdd={loadOrderDetails}
                showAdd={serviceRequest.status === 'active'}
          />
          <PaymentSection
            serviceRequest={serviceRequest}
            patient={patient ?? undefined}
              />

              {/* Show consent display when consent exists */}
              {consentStatus && (
                <ConsentDisplay
                  key={`consent-${serviceRequest.id}-${consentUpdateKey}`}
                  serviceRequest={serviceRequest}
                  onEdit={openConsentModal}
                />
              )}

              {/* Consent Modal */}
              {showConsentModal && (
                <ConsentSection
                  serviceRequest={serviceRequest}
                  patient={orderDetails.patient}
                  editMode={true}
                  onConsentUpdate={handleConsentUpdate}
                  showModal={showConsentModal}
                  onCloseModal={closeConsentModal}
                />
              )}
            </Stack>

            {specimens.length > 0 && (
              <ReportBox serviceRequest={serviceRequest} />
            )}
          </Group>

          <Group justify="flex-start">
            <Button variant="subtle" onClick={() => navigate('/lims')}>
              {`← ${t('orderDetails.backToOrders', { ns: 'lims' })}`}
            </Button>
          </Group>
        </Stack>
      </Box>
    </Box>
  );
};
